import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Phone, Mail, Plus, Edit2, Trash2, Search } from "lucide-react";
import { CONTENT } from "@/constants/testIds";
import { useDepartments } from "@/hooks/useDepartments";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });

const deptColors = {
  "RH": "#3b82f6", "TI": "#8b5cf6", "Marketing": "#ec4899", "Financeiro": "#10b981",
  "Fiscal/Contábil": "#f59e0b", "Jurídico": "#ef4444", "Logística": "#06b6d4",
  "Crédito e Cobrança": "#84cc16", "Compras/Comercial": "#f97316", "Marketing": "#a855f7"
};

export default function ContactsPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const { departments } = useDepartments();
  const DEPTS = departments;
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("__all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", department: "", phone: "", email_contact: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => ax().get(`${API}/contacts`).then(r => setItems(r.data)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const filtered = items.filter(i => {
    const matchSearch = i.title?.toLowerCase().includes(search.toLowerCase()) || i.department?.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "__all" || i.department === deptFilter;
    return matchSearch && matchDept;
  });

  const openCreate = () => { setEditing(null); setForm({ title: "", content: "", department: "", phone: "", email_contact: "" }); setShowForm(true); };
  const openEdit = (item) => { setEditing(item); setForm({ title: item.title, content: item.content, department: item.department || "", phone: item.phone || "", email_contact: item.email_contact || "" }); setShowForm(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await ax().put(`${API}/contacts/${editing.item_id}`, form);
      else await ax().post(`${API}/contacts`, form);
      setShowForm(false); load();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este contato?")) return;
    await ax().delete(`${API}/contacts/${id}`);
    load();
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Phone className="w-5 h-5 text-[#2346ff]" />
          <div>
            <h1 className="text-xl font-bold font-heading text-slate-900">Contatos Internos</h1>
            <p className="text-sm text-slate-500">{items.length} contatos cadastrados</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="text-white gap-2">
            <Plus className="w-4 h-4" /> Novo Contato
          </Button>
        )}
      </div>

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Buscar contatos..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={deptFilter} onValueChange={setDeptFilter}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Departamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Todos</SelectItem>
            {DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div data-testid={CONTENT.contentList} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-12 text-slate-400">
              <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum contato encontrado</p>
            </div>
          )}
          {filtered.map(item => {
            const color = deptColors[item.department] || "#2346ff";
            return (
              <div key={item.item_id} data-testid={CONTENT.contentItem} className="bento-card p-4 group">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: color + "20" }}>
                    <span style={{ color }}>{item.title.charAt(0)}</span>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => remove(item.item_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-slate-800 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{item.content}</p>
                {item.department && (
                  <Badge className="text-xs mt-2" style={{ backgroundColor: color + "20", color }}>{item.department}</Badge>
                )}
                <div className="mt-3 space-y-1.5">
                  {item.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <a href={`tel:${item.phone}`} className="text-xs text-slate-600 hover:text-[#2346ff] transition-colors">{item.phone}</a>
                    </div>
                  )}
                  {item.email_contact && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <a href={`mailto:${item.email_contact}`} className="text-xs text-slate-600 hover:text-[#2346ff] transition-colors truncate">{item.email_contact}</a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar Contato" : "Novo Contato"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome/Setor *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
            <div><Label>Descrição</Label><Textarea rows={2} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} /></div>
            <div>
              <Label>Departamento</Label>
              <Select value={form.department || "__none"} onValueChange={v => setForm(f => ({...f, department: v === "__none" ? "" : v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none">—</SelectItem>{DEPTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefone</Label><Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="(11) 0000-0000" /></div>
              <div><Label>E-mail</Label><Input value={form.email_contact} onChange={e => setForm(f => ({...f, email_contact: e.target.value}))} placeholder="email@..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.title} style={{ backgroundColor: "#2346ff" }} className="text-white">{saving ? "Salvando..." : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
